/* eslint-disable camelcase */
import BaseChart from '../common/BaseChart'

import { schemePaired } from 'd3-scale-chromatic'
import { css } from 'emotion'
import { pointer, Selection } from 'd3-selection'
import { scaleLinear, scaleLog } from 'd3-scale'
import Trajectory from './engine/Trajectory'
import WBSRender from './engine/WBSRender'
import { D3ZoomEvent, zoom, zoomIdentity, ZoomTransform } from 'd3-zoom'
import { calculateBHA, bhaLength } from '../bha/calculateBHA'
import SegmentTree, { equation, intersection } from './engine/SegmentTree'
import LithologyPatterns from '../lithology/LithologyPatterns'
import { bisector, extent } from 'd3-array'
import { renderTooltip } from './TooltipLayout'
import PointService from 'live/services/charts/point'
import { throttle } from 'lodash'
import { format } from 'd3-format'
import {
    DataTrajectoryPoint,
    GammaResistivity,
    WBSState,
    SideChartChannels,
    TooltipData,
    LithologyPoint,
    WbsOptions
} from './types'
import ReactDOM from 'react-dom'
import React from 'react'
import WbsOverlay from './WbsOverlay'
import loggerFactory from 'live/services/logger'
import { channelsColors, zoomOptions, ZoomStatus } from './utils'
import { BHA } from 'webapp/js/types'
import paper from 'paper'

const logger = loggerFactory('wbs-chart')
const highlightAll = throttle(x => PointService.highlightAll({ x }, false), 100)

const bitDepthFormat = format('.02f')

const errorColor = '#D9534F'

export default class WBSChart extends BaseChart<WBSState> {
    public tooltip: HTMLElement
    private canvas: Selection<HTMLCanvasElement, unknown, null, undefined>
    private canvas2: Selection<HTMLCanvasElement, unknown, null, undefined>

    protected transform: ZoomTransform = zoomIdentity

    protected data: WBSState | undefined
    protected lithologyPatterns: LithologyPatterns
    private _render?: WBSRender
    private renderOptions: WbsOptions
    private trajectory?: Trajectory
    private tree: SegmentTree

    private axisMargin = 65
    private bitDepth: number | null = null
    protected canvasMargin = 10
    protected canvasHeight: number
    private maxArea?: number

    private syncTooltip: boolean
    private userZoomed?: boolean

    private focusPointCorrection?: { x: number; y: number }
    private filter: string
    private dashTheme: 'light' | 'dark'
    private zoomStatus: ZoomStatus

    init(): void {
        if (this.el.node()) {
            this.lithologyPatterns = new LithologyPatterns()
            this.el.classed(`trajectory-chart`, true).classed(trajectoryChartCss, true)

            this.tooltip = this.el
                .append('div')
                .classed('wbs-tooltip', true)
                .attr('aria-label', 'widget tooltip')
                .node() as HTMLElement

            // create canvas.
            this.canvas = this.el
                .append('canvas')
                .classed('data', true)
                .style('z-index', 0)
                .style('position', 'absolute')
            this.canvas2 = this.el
                .append('canvas')
                .classed('ui', true)
                .style('z-index', 0)
                .style('position', 'absolute')

            // it is important to set the filter and zoom status in the init to avoid the onChange config to run on the first render
            this.filter = this.options.activeFilters?.asset_name_event_type
                ? this.options.activeFilters?.asset_name_event_type[0]
                : ''
            this.zoomStatus = this.options.cfg.zoomStatus

            // this listens to mouse events on other charts and triggers the response from wbs
            if (this.options.mode !== 'editor') {
                PointService.addEventListener('mouseover', this.respondToMouseOverInOtherCharts)
                PointService.addEventListener('mouseleave', this.onMouseLeave)
            }
        }
    }

    get bhaWidth(): number {
        return this.maxArea ? this.maxArea * 0.04 : 0
    }

    setCanvasDimentions(canvas: Selection<HTMLCanvasElement, unknown, null, undefined>): void {
        if (canvas === null) return

        // the pixel ratio will help with the blury text
        canvas
            .style('bottom', 0 + 'px')
            .style('left', 0 + 'px')
            .attr('width', Math.floor(this.width * (this._render?.pixelRatio || 1)) + 'px')
            .attr('height', Math.floor(this.canvasHeight * (this._render?.pixelRatio || 1)) + 'px')
            .style('width ', this.width + 'px')
            .style('height', this.canvasHeight + 'px')
    }

    private checkAvailableDistance(trajectory: Trajectory, maxWidth: number): number {
        const availableDistance = trajectory.getAvailableDistance()

        if (availableDistance > maxWidth / 2 && availableDistance < maxWidth) {
            return availableDistance
        }

        return maxWidth
    }

    setFocusPointCorrection(): void {
        let focusPoint = 0

        if (this.zoomStatus === zoomOptions.followBitDepth) {
            focusPoint = this.data?.bitdepth

            if (this.data.bha && Array.isArray(this.data.bha) && this.data.bha.length > 0) {
                const bha = this.data.bha[0].bha
                if (Array.isArray(bha)) {
                    const length = bhaLength(bha.filter(c => c.component_type !== 'Drill pipe'))
                    // If the BHA is too long, we choose to focus on the position of the bit.
                    // Otherwise, we focus on the center of the BHA.
                    if (length < 500) {
                        focusPoint -= length / 2
                    }
                }
            }
        } else if (this.zoomStatus === zoomOptions.zoomEntireWell) {
            focusPoint = this.trajectory.maxMD / 2
        }

        const focusPointPosition = this.trajectory.getPoint(focusPoint, 0, 'md')
        this.focusPointCorrection = {
            x: -focusPointPosition.x + this.width / 2,
            y: -focusPointPosition.y + this.canvasHeight / 2
        }
    }

    // this function is called every time there is a new event in the widget
    render(data: WBSState, options: WbsOptions): void {
        if (!data || (!options.filter && this.options.mode !== 'editor')) {
            return
        }

        this.data = data

        this.trajectory = new Trajectory(data.trajectory, data.depthMD)

        this.canvasHeight = this.height - this.tooltip?.getBoundingClientRect().height - this.canvasMargin

        if (options) {
            this.renderOptions = options
            if (options.dashboardTheme !== this.dashTheme) renderTooltip({}, this.tooltip, {}, options.dashboardTheme)
            this.dashTheme = options.dashboardTheme || 'light'
            this.syncTooltip = options.widget.jsonConfig.syncTooltip
            if (this.zoomStatus !== options.zoomStatus || this.filter !== options.filter) {
                this.zoomStatus = options.widget.jsonConfig.zoomStatus
                if (this.zoomStatus !== zoomOptions.free) this.onChangeConfig(options)
            }
        }

        this.setFocusPointCorrection()

        if (this.focusPointCorrection && !this.userZoomed) {
            this.translateCanvasToFocusPoint()
            this.adjustCanvasZoom()
        }

        if (data.lithology) {
            this.lithologyPatterns.load(data.lithology, () => {
                this.render(data, options)
            })
        }

        const maxArea = data.depthMD === 'ft' ? 500 * 3.28084 : 500

        this.maxArea = this.checkAvailableDistance(this.trajectory, maxArea)

        this.tree = new SegmentTree(this.trajectory, this.maxArea)

        if (this.canvas) this.setCanvasDimentions(this.canvas)

        if (this.canvas2) {
            this.setCanvasDimentions(this.canvas2)

            this.canvas2.on('mousemove', this.onMouseMove)

            this.canvas2.call(this.chartZoom)
        }

        this.renderWBS()
    }

    translateCanvasToFocusPoint(): void {
        this.canvas2.call(
            this.chartZoom.transform,
            zoomIdentity.translate(this.focusPointCorrection?.x || 0, this.focusPointCorrection?.y || 0)
        )
    }

    adjustCanvasZoom(): void {
        const aspect = this.width / this.canvasHeight

        switch (this.zoomStatus) {
            case zoomOptions.zoomEntireWell:
                const leftTop = this.trajectory.getPoint(0, -this.maxArea, 'md')

                const lastPointRight = this.trajectory.getPoint(this.trajectory.maxMD, this.maxArea, 'md')
                const lastPointLeft = this.trajectory.getPoint(this.trajectory.maxMD, -this.maxArea, 'md')

                // TODO Find bounding box of the whole well
                const wellWidth = Math.max(lastPointRight.x, lastPointLeft.x) - leftTop.x
                const wellHeight = Math.max(lastPointRight.y, lastPointLeft.y)

                if (!wellHeight) return // nothing to fit

                // The correct thing would be to use unitary factor and that would generate the zoom for the whole well
                // But there is some weird interaction when applying the transformation into the canvas
                // We need to review the WBSRender to use linear scales to draw on the canvas (and define the viewport
                // through them) and apply the zoom transformation on the scales when necessary
                const marginFactorScale = scaleLinear().domain([0.6, 1.5, 1.7, 3]).range([0.3, 0.75, 0.55, 0.6])
                const marginFactor = marginFactorScale(aspect)
                const zoomScale = marginFactor / Math.max(wellWidth / this.width, wellHeight / this.canvasHeight)

                this.canvas2.call(this.chartZoom.scaleBy, zoomScale)

                break
            case zoomOptions.followBitDepth:
                this.canvas2.call(this.chartZoom.scaleBy, aspect < 1 ? 0.2 : 0.4)

                break

            default:
                this.canvas2.call(this.chartZoom.scaleBy, 0.5)
        }
    }

    onChangeConfig(configs: WbsOptions): void {
        if (this.zoomStatus || this.filter !== configs.filter) {
            this.filter = configs.filter

            this.userZoomed = false

            this.setFocusPointCorrection()

            this.translateCanvasToFocusPoint()
            this.adjustCanvasZoom()
            this.renderWBS()
        }
    }

    private onZoom = ({ transform, sourceEvent }: D3ZoomEvent<HTMLCanvasElement, unknown>): void => {
        this.transform = transform
        // checking for the source event makes sure that this func was triggered by the user and a mouse action
        if (sourceEvent) {
            // this check is necessary because the widgets works differently on edit mode
            if (this.options.mode !== 'editor') {
                if (this.renderOptions.widget.jsonConfig.zoomStatus !== zoomOptions.free) {
                    // changing the config triggers a render
                    this.renderOptions.changeConfig('zoomStatus', zoomOptions.free)
                } else {
                    this.renderWBS()
                }
            } else {
                this.zoomStatus = zoomOptions.free
                this.renderWBS()
            }
            this.userZoomed = true
        }
    }

    private chartZoom = zoom<HTMLCanvasElement, unknown>()
        // this hides the mouse indicator when zooming so it doesnt grow/shrink disproportionally
        .on('start', () => {
            const canvas = this.canvas2.node()
            if (canvas) canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height)
        })
        .on('zoom', this.onZoom)

    destroy(): void {
        if (this.tooltip) {
            this.tooltip.remove()
        }
        if (this.canvas) {
            this.canvas.remove()
        }
        if (this.canvas2) {
            this.canvas2.remove()
        }
        this.el.selectAll('div.icons').remove()
        this.el.selectAll('.warningOverlay').remove()
        this.el.attr('class', null)
        try {
            PointService.removeEventListener('mouseover', this.respondToMouseOverInOtherCharts)
            PointService.removeEventListener('mouseleave', this.onMouseLeave)
        } catch (e) {
            console.error(e)
        }
    }

    private onMouseLeave = (): void => {
        const canvas2 = this.canvas2.node()
        if (canvas2) {
            const ctx2 = canvas2.getContext('2d')
            ctx2?.clearRect(0, 0, canvas2.width, canvas2.height)
            renderTooltip({}, this.tooltip, {}, this.dashTheme)
        }
    }

    private respondToMouseOverInOtherCharts = (mouseEvent: MouseEvent, chart: any): void => {
        if ((this.options.cfg?.syncTooltip ?? false) && mouseEvent && mouseEvent.target && chart !== this) {
            const md = mouseEvent.target.x
            this._render?.renderMouseOverMarker(md, this.maxArea, this.canvas2.node())
            this.updateTooltipInfo(md)
        }
    }

    private onMouseMove = throttle((event): void => {
        const [originalMouseX, originalMouseY] = pointer(event, this.canvas2.node())

        const adjustedMouseX = (originalMouseX - this.transform.x) / this.transform.k
        const adjustedMouseY = (originalMouseY - this.transform.y - this.axisMargin) / this.transform.k

        // if md is not found, either because the point doesn't exist or because the mouse is outside the chart area
        // the widget should use the min md from the trajectory to update the tooltip and marker
        const md =
            Number(bitDepthFormat(this.tree.getMD([adjustedMouseX, adjustedMouseY]) || 0)) || this.trajectory.minMD

        this._render?.renderMouseOverMarker(md, this.maxArea, this.canvas2.node())

        if (!isNaN(md)) {
            this.updateTooltipInfo(md)

            if (this.syncTooltip ?? false) {
                highlightAll(md)
                PointService.emit('mouseover', { target: { x: md } }, this, [])
            }
        } else {
            this.onMouseLeave()

            if (this.syncTooltip ?? false) {
                PointService.emit('mouseleave')
                PointService.hideAllHighlighted(this)
            }
        }
    })

    private updateTooltipInfo(md: number): void {
        const render = this._render
        const data = this.data

        if (!render || !data) return

        const finder = bisector((d: GammaResistivity | DataTrajectoryPoint) => d.md).left
        const tooltipData: TooltipData = {
            md
        }

        const sideChartChannels: SideChartChannels[] = ['resistivity1', 'resistivity2', 'gamma']

        sideChartChannels.forEach(channel => {
            if (data[channel]) {
                const found = finder(data[channel], md)
                if (data[channel][found]) {
                    tooltipData[channel] = data[channel][found].value
                }
            }
        })

        if (data.trajectory) {
            const found = finder(data.trajectory, md)
            tooltipData.trajectory = data.trajectory[found]
        }

        if (data.lithology) {
            const finder2 = bisector((d: LithologyPoint) => d.top).right
            const found = finder2(data.lithology, md)

            if (found) {
                tooltipData.lithology = data.lithology[found - 1]
            }
        }

        if (data.bha && this.currentBitDepth && Array.isArray(data.bha) && data.bha.length > 0) {
            const finder2 = bisector((d: BHA) => d.top).left
            const bha = calculateBHA(data.bha[0].bha, this.currentBitDepth)
            const found = finder2(bha, md)
            if (bha[found - 1]) {
                tooltipData.bha = bha[found - 1]
            }
        }

        renderTooltip(tooltipData, this.tooltip, data.uom, this.dashTheme)
    }

    get currentBitDepth(): number {
        return this.data?.bitdepth ? this.data.bitdepth : 0
    }

    private renderOverlay(): void {
        const parent = this.el.node()

        let container = parent?.querySelector('.warningOverlay')
        if (container == null) {
            container = document.createElement('div')
            container.className = 'warningOverlay'

            parent?.appendChild(container)
        }

        ReactDOM.render(
            React.createElement(WbsOverlay, {
                theme: this.dashTheme,
                height: this.height,
                show: this.trajectory.getAvailableDistance() < this.maxArea
            }),
            container
        )
    }

    private renderWBS(): void {
        const ts = performance.now()

        if (this.trajectory.hasPoints()) {
            const node = this.canvas.node()
            const data = this.data

            if (!node || !data) return

            const render = new WBSRender(
                node,
                this.trajectory,
                this.width,
                this.canvasHeight,
                this.transform,
                this.axisMargin,
                this.dashTheme
            )
            this._render = render

            const maxMD = Math.max(this.trajectory.maxMD, this.currentBitDepth || 0)

            // TODO: improve responsibility
            const holeSize = [0.128, 0.108, 0.088, 0.068].map(p => p * this.maxArea)
            const mdAxisSize = 0.256 * this.maxArea
            const lithologyRange = [0.13, 0.256].map(p => p * this.maxArea)
            const drillStringWidth = this.maxArea * 0.02
            const channelWidth = [0.4, 1].map(p => p * this.maxArea)

            this.renderOverlay()

            // well chart grig bg
            render.renderArea(0, maxMD, -this.maxArea, this.maxArea, '#fff')

            // casing segment.
            render.renderArea(0, maxMD, -holeSize[0], holeSize[0], '#ccc')
            render.renderArea(0, maxMD, -holeSize[1], holeSize[1], '#aaa')
            render.renderArea(0, maxMD, -holeSize[2], holeSize[2], '#999')
            render.renderArea(0, maxMD, -holeSize[3], holeSize[3], '#888')

            if (localStorage.getItem('debug-wbs-trajectory') != null) {
                // Only for DEBUG: Render trajectory points marking the segments with problem
                render.renderTrajectory(
                    this.trajectory.getTrajectorySegment(this.trajectory.minMD, maxMD, -lithologyRange[1] - 10)
                )

                // Only for DEBUG: simplified trajectory
                render.renderTrajectory(
                    this.trajectory.getSegment(this.trajectory.minMD, maxMD, -lithologyRange[1] - 20)
                )
                for (let i = 300; i <= this.maxArea; i += 10) {
                    render.renderTrajectory(this.trajectory.getSegment(this.trajectory.minMD, maxMD, -i))
                }
                const path = this.trajectory.getSimplified()
                render.renderTrajectory(path.segments.map(s => new paper.Point(s.point.x, s.point.y)))
                const segments = 100
                let f1
                let availableDistance = Number.POSITIVE_INFINITY
                let availableDistancePoint
                for (let i = 0; i < segments; i++) {
                    const offset = path.length * (i / segments)

                    const point = path.getPointAt(offset)
                    const normal = path.getNormalAt(offset).multiply(this.maxArea)

                    render.ctx.lineWidth = 1
                    render.renderLine([point, point.add(normal)], 'red')
                    render.renderLine([point, point.subtract(normal)], 'green')

                    const f2 = equation(point, point.subtract(normal))
                    if (f1) {
                        const intersectionPoint = intersection(f1, f2)
                        render.renderLine([point, intersectionPoint], 'blue')
                        const distance = new paper.Point(intersectionPoint.x, intersectionPoint.y).subtract(point)
                            .length

                        if (distance < availableDistance) {
                            availableDistance = distance
                            availableDistancePoint = intersectionPoint
                        }
                    }
                    f1 = f2
                }
                console.debug('trajectory available distance', availableDistance, availableDistancePoint)
            }

            render.ctx.lineWidth = 1
            render.renderLine(this.trajectory.getSegment(0, maxMD, holeSize[0]), '#333')
            render.renderLine(this.trajectory.getSegment(0, maxMD, -holeSize[0]), '#333')

            render.renderMDAxis(mdAxisSize)
            render.renderMDAxis(-mdAxisSize)

            if (data.lithology) {
                render.renderLine(this.trajectory.getSegment(0, maxMD, lithologyRange[1]), '#333')
                const xScale = scaleLinear().domain([0, 1.0]).range(lithologyRange)

                data.lithology.forEach(item => {
                    let lithPcTotalAcc = 0

                    const lithPcTotal = item.lithologys.reduce((state: number, obj: { lithPc: { value: number } }) => {
                        return state + obj.lithPc.value
                    }, 0)
                    for (let x = 0; x < item.lithologys.length; x++) {
                        const lithology = item.lithologys[x]
                        const lithPc = lithology.lithPc.value

                        const fillX = xScale(lithPcTotalAcc)
                        const fillWidth = xScale(lithPcTotalAcc + lithPc / lithPcTotal)
                        const code = lithology.code

                        lithPcTotalAcc += lithPc / lithPcTotal

                        const color = lithology.color === 'white' ? '#f0f' : lithology.color

                        render.renderArea(item.top, item.bottom, fillX, fillWidth, color)
                        render.renderArea(item.top, item.bottom, -fillX, -fillWidth, color)

                        // fill pattern
                        if (this.lithologyPatterns.has(code) && fillWidth > 1) {
                            const fillStyle = this.lithologyPatterns.getCanvasPattern(code, render.ctx)
                            if (fillStyle) {
                                render.renderArea(item.top, item.bottom, fillX, fillWidth, fillStyle)
                                render.renderArea(item.top, item.bottom, -fillX, -fillWidth, fillStyle)
                            }
                        }
                    }
                })
            }

            // lithology line.
            render.ctx.lineWidth = 2
            render.renderLine(this.trajectory.getSegment(0, maxMD, -lithologyRange[0]), '#333')
            render.renderLine(this.trajectory.getSegment(0, maxMD, lithologyRange[0]), '#333')

            render.ctx.lineWidth = 2
            this.trajectory
                .getProblemSegments(this.maxArea, lithologyRange[0])
                .forEach(s => render.renderPathLine(s, errorColor))
            this.trajectory
                .getProblemSegments(this.maxArea, -lithologyRange[0])
                .forEach(s => render.renderPathLine(s, errorColor))

            render.ctx.lineWidth = 1

            if (data.sections.length > 0) {
                const sections = data.sections
                const maxDiameter = sections[0].bit_diameter

                if (maxDiameter !== undefined) {
                    sections.forEach(s => {
                        if (s.md_casing_shoe !== undefined && s.bit_diameter !== undefined) {
                            const diameter = (s.bit_diameter / maxDiameter) * holeSize[0]
                            render.renderCasing(0, s.md_casing_shoe, diameter, this.maxArea * 0.03)
                        }
                    })
                }
            }

            try {
                render.renderArea(0, this.currentBitDepth || 0, -drillStringWidth, drillStringWidth, '#333')
                if (Array.isArray(data.bha) && data.bha.length > 0 && this.currentBitDepth) {
                    const bha = calculateBHA(data.bha[0].bha, this.currentBitDepth)

                    for (const index in bha) {
                        const component = bha[index]
                        render.renderArea(
                            component.top || 0,
                            component.bottom || 0,
                            -this.bhaWidth,
                            this.bhaWidth,
                            schemePaired[index]
                        )
                    }
                }
                render.ctx.lineWidth = 1
            } catch (e) {
                console.error(e)
            }

            if (data.gamma) {
                const gammaExtent = extent(data.gamma, p => p.value)

                const s = scaleLinear()
                    .domain(gammaExtent.some(e => e === undefined) ? [0, 1] : gammaExtent)
                    .range([-channelWidth[0], -channelWidth[1]])
                render.renderGrid(-channelWidth[0], -channelWidth[1], 6, s)
                render.renderLine(this.trajectory.getChannel(data.gamma, s), channelsColors.gamma)
            }

            if (data.resistivity1) {
                const resistivity1Extent = extent(data.resistivity1, p => p.value)

                const s = scaleLog()
                    .domain(resistivity1Extent.some(e => e === undefined) ? [0.1, 1] : resistivity1Extent)
                    .range([channelWidth[0], channelWidth[1]])
                render.renderGrid(channelWidth[0], channelWidth[1], 6, s)
                render.renderLine(this.trajectory.getChannel(data.resistivity1, s), channelsColors.resistivity1)
            }

            if (data.resistivity2) {
                const resistivity2Extent = extent(data.resistivity2, p => p.value)
                const s = scaleLog()
                    .domain(resistivity2Extent.some(e => e === undefined) ? [0.1, 1] : resistivity2Extent)
                    .range([channelWidth[0], channelWidth[1]])

                render.renderLine(this.trajectory.getChannel(data.resistivity2, s), channelsColors.resistivity2)
            }

            render.renderTVDAxis(this.canvas.node().getContext('2d'))

            // this restore is necessary to return to the context state saved in the render constructor
            render.finalrestore()
        }

        const elapsed = performance.now() - ts
        if (elapsed > 1000) {
            console.log('WBS render:', elapsed)
        }
    }
}

const trajectoryChartCss = css`
    position: relative;
    padding: 0 !important;
    svg {
        top: 0;
        position: absolute;
    }

    .wbs-tooltip {
        height: 140px;
    }

    canvas {
        //border: 1px solid green;
        position: absolute;

        cursor: move;
        cursor: -webkit-grab;
        cursor: -moz-grab;

        &:active {
            cursor: move;
            cursor: -webkit-grabbing;
            cursor: -moz-grabbing;
        }
    }
`
